/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import { Component, computed, OnInit, signal, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { EChartsOption } from "echarts";
import { finalize, forkJoin, last, map, repeat, switchMap, takeWhile, tap, throwError } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../../shared/components/input/input.component";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { TableComponent } from "../../../shared/components/table/table.component";
import { BarChart } from "../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { PieChart } from "../../../shared/models/http/charts/pie-chart/pie-chart.model";
import { CalculatorHardwareAssetsForm } from "../../../shared/models/http/dashboard/calculator-hardware-assets-form.model";
import { HardwareAssetsOverview } from "../../../shared/models/http/dashboard/hardware-assets-overview.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { HardwareComponent } from "../../../shared/models/ui/hardware-component.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { CalculatorService } from "../../../shared/services/calculator.service";
import { DashboardHardwareAssetsService } from "../../../shared/services/dashboard-hardware-assets.service";
import { StorageService } from "../../../shared/services/storage.service";
import { getHardwareAssetsTotalEmissionOverridesForPieChart } from "../../../shared/utils/echarts/chart-utils";
import { roundValueUpTo } from "../../../shared/utils/utils";
import { CARBON_FOOTPRINT_BAR_CHART_OVERRIDES } from "../../../shared/utils/echarts/chart-constants";
import { ApplicationError } from "../../../shared/models/ui/application-error.model";
import { COLORS } from "../../../shared/utils/constants";

@Component({
	selector: "globalictcc-calculator-hardware-assets",
	standalone: true,
	imports: [
		NgClass,
		DropdownComponent,
		ButtonComponent,
		SpinnerComponent,
		TableComponent,
		ChartComponent,
		ReactiveFormsModule,
		InputComponent
	],
	templateUrl: "./calculator-hardware-assets.component.html",
	styleUrl: "./calculator-hardware-assets.component.scss"
})
export class CalculatorHardwareAssetsComponent implements OnInit, OnDestroy {
	private _emissionByScopePieChartTotalEmissions = signal<number>(0);
	private _emissionByManufacturerPieChartTotalEmissions = signal<number>(0);

	public generalCategoriesOptions: SelectOption[] = [];
	public categoriesOptions: SelectOption[] = [];
	public manufacturersOptions: SelectOption[] = [];
	public locationsOptions: SelectOption[] = [];
	public partsCountOptions: SelectOption[] = [];

	public isLoading: boolean = false;
	public hasError: boolean = false;
	public isErrorLogOpened: boolean = false;
	public errorLogMessage: string = "Something went wrong";
	public hasCalculated: boolean = false;
	public hardwareComponents: HardwareComponent[] = [
		{
			id: uuidv4(),
			form: new FormGroup({
				manufacturerPartNumber: new FormControl("", [Validators.required]),
				manufacturer: new FormControl("", [Validators.required]),
				generalCategory: new FormControl("", [Validators.required]),
				category: new FormControl("", [Validators.required]),
				location: new FormControl("", [Validators.required]),
				partsCount: new FormControl("", [Validators.required])
			}),
			categoriesOptions: []
		}
	];

	public emissionGridOptions: GridOptions<HardwareAssetsOverview> = {
		columnDefs: [
			{ headerName: "Manufacturer name", field: "manufacturer" },
			{ headerName: "Model number", field: "manufacturerPartNumber" },
			{ headerName: "Parts count", field: "partsCount" },
			{ headerName: "Scope 2 kg CO2eq", field: "scope2Co2e" },
			{ headerName: "Scope 3 kg CO2eq", field: "scope3Co2e" },
			{ headerName: "Total kg CO2eq", field: "totalCo2e" }
		],
		rowData: [],
		onGridReady: this._onGridReady.bind(this)
	};

	public totalEmissionPieChartColors: string[] = [];
	public overviewTableHasError: boolean = false;
	public emissionByScopePieChart?: ChartResponse<PieChart>;
	public isEmissionByScopePieChartLoading: boolean = false;
	public emissionByModelPieChart?: ChartResponse<PieChart>;
	public isEmissionByModelPieChartLoading: boolean = false;
	public totalEmissionBarChart?: ChartResponse<BarChart>;
	public isTotalEmissionBarChartLoading: boolean = false;
	public totalEmissionBarChartOverrides: EChartsOption = CARBON_FOOTPRINT_BAR_CHART_OVERRIDES;
	public emissionByScopePieChartOverrides = computed(() =>
		getHardwareAssetsTotalEmissionOverridesForPieChart(
			this._emissionByScopePieChartTotalEmissions()
		)
	);
	public emissionByManufacturerPieChartOverrides = computed(() =>
		getHardwareAssetsTotalEmissionOverridesForPieChart(
			this._emissionByManufacturerPieChartTotalEmissions()
		)
	);

	private _predefinedColors = [
		COLORS.GREEN_50,
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100,
		COLORS.ORANGE_50,
		COLORS.TURQUOISE_50
	];
	private _calculationId: string = "";
	private _tenantId: string = "";
	private _gridApi: GridApi<HardwareAssetsOverview>;

	constructor(
		private _dashboardHardwareAssetsService: DashboardHardwareAssetsService,
		private _calculatorService: CalculatorService,
		private _storageService: StorageService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		this._getHardwareAssetsOptions();
	}

	ngOnDestroy() {
		this._deleteCalculation();
	}

	/**
	 * Handles the AG Grid's grid ready event.
	 *
	 * This method is triggered when the grid is fully initialized and ready to interact with.
	 * It stores the grid API instance for later use and initiates the emission overview retrieval process.
	 *
	 * @param params - The event parameters containing the grid API and other relevant information.
	 */
	private _onGridReady(params: GridReadyEvent) {
		this._gridApi = params.api;

		this.onTableRefresh();
	}

	/**
	 * Determines whether the calculation action should be disabled.
	 *
	 * @returns {boolean} `true` if there are no hardware components or if any hardware component's form is invalid; otherwise, `false`.
	 */
	public isCalculationDisabled(): boolean {
		return (
			!this.hardwareComponents.length ||
			this.hardwareComponents.some(component => component.form.invalid)
		);
	}

	/**
	 * Adds a new hardware component entry to the `hardwareComponents` array.
	 *
	 * Each entry consists of:
	 * - A unique `id` generated by `uuidv4()`.
	 * - A `form` property, which is a `FormGroup` containing form controls for:
	 *   - `manufacturerPartNumber`
	 *   - `manufacturer`
	 *   - `generalCategory`
	 *   - `category`
	 *   - `location`
	 *   - `partsCount`
	 *   All controls are initialized with an empty string and marked as required.
	 * - An empty `categoriesOptions` array.
	 *
	 * This method is typically used to allow users to add new hardware components dynamically in the UI.
	 */
	public onAddHardwareComponent() {
		this.hardwareComponents.push({
			id: uuidv4(),
			form: new FormGroup({
				manufacturerPartNumber: new FormControl("", [Validators.required]),
				manufacturer: new FormControl("", [Validators.required]),
				generalCategory: new FormControl("", [Validators.required]),
				category: new FormControl("", [Validators.required]),
				location: new FormControl("", [Validators.required]),
				partsCount: new FormControl("", [Validators.required])
			}),
			categoriesOptions: []
		});
	}

	/**
	 * Removes a hardware component from the `hardwareComponents` array by its unique identifier.
	 *
	 * @param id - The unique identifier of the hardware component to remove.
	 */
	public onRemoveHardwareComponent(id: string) {
		this.hardwareComponents = this.hardwareComponents.filter(component => component.id !== id);
	}

	/**
	 * Refreshes the hardware assets table by fetching the latest emission overview data.
	 * Invokes the private `_getEmissionOverview` method and subscribes to its observable
	 * to trigger the data retrieval process.
	 */
	public onTableRefresh() {
		this._getEmissionOverview().subscribe();
	}

	/**
	 * Handles the refresh action for the emission by scope chart.
	 * Sets the loading state for the total emission bar chart and triggers
	 * the retrieval of total emission data by subscribing to the observable
	 * returned by `_getTotalEmission()`.
	 */
	public onEmissionByScopeChartRefresh() {
		this.isTotalEmissionBarChartLoading = true;

		this._getTotalEmission().subscribe();
	}

	/**
	 * Handles the refresh action for the "Emission By Model" chart.
	 * Sets the loading state to true and triggers the retrieval of emission data by manufacturer.
	 * Typically called when the user requests to refresh the chart data.
	 */
	public onEmissionByModelChartRefresh() {
		this.isEmissionByModelPieChartLoading = true;

		this._getEmissionByManufacturer().subscribe();
	}

	/**
	 * Handles the refresh action for the total emission chart.
	 *
	 * Sets the loading state for the emission by scope pie chart and triggers
	 * the retrieval of emission data by scope.
	 *
	 * @remarks
	 * This method is typically called when the user requests to refresh the emission chart,
	 * ensuring that the latest data is fetched and the loading indicator is displayed.
	 */
	public onTotalEmissionChartRefresh() {
		this.isEmissionByScopePieChartLoading = true;

		this._getEmissionByScope().subscribe();
	}

	/**
	 * Handles the change event for the general hardware category selection.
	 *
	 * Fetches the specific categories related to the selected general category
	 * using the DashboardHardwareAssetsService, transforms them into options
	 * suitable for a dropdown, and updates the corresponding hardware component's
	 * category options and resets its category form control.
	 *
	 * @param generalCategory - The selected general category or categories as a string or array of strings.
	 * @param hardwareComponentId - The unique identifier of the hardware component to update.
	 */
	public onGeneralCategoryChange(
		generalCategory: string | string[],
		component: HardwareComponent
	) {
		const selectedGeneralCategory = generalCategory as string;

		this._dashboardHardwareAssetsService
			.getSpecificCategories(selectedGeneralCategory)
			.pipe(
				map(({ categories }) =>
					categories.map(category => ({
						label: category,
						value: category
					}))
				),
				tap({
					next: categories => {
						if (component) {
							component.categoriesOptions = categories;
							component.form.patchValue({ category: "" });
						}
					}
				})
			)
			.subscribe();
	}

	/**
	 * Initiates the calculation process for hardware asset emissions.
	 *
	 * This method collects form data from all hardware components, triggers the emission calculation
	 * via the dashboard hardware assets service, and handles the loading and error states accordingly.
	 * Upon successful calculation, it retrieves various emission-related data (overview, total, by scope,
	 * by manufacturer, and KPIs) using parallel API calls.
	 *
	 * @remarks
	 * - Sets `isLoading` to true at the start and resets it upon completion.
	 * - Updates `hasError` and `errorLogMessage` if the calculation fails.
	 * - Stores the calculation ID for further data retrieval.
	 * - Updates `hasCalculated` to indicate completion.
	 */
	public onCalculate() {
		this.isLoading = true;

		const hardwareComponents: CalculatorHardwareAssetsForm[] = this.hardwareComponents.map(
			component => ({
				manufacturer: component.form.value.manufacturer,
				manufacturerPartNumber: component.form.value.manufacturerPartNumber,
				category: component.form.value.category,
				location: component.form.value.location,
				partsCount: component.form.value.partsCount
			})
		);

		this._dashboardHardwareAssetsService
			.compute(hardwareComponents)
			.pipe(
				tap({
					next: ({ calculationId }) => {
						this.hasError = false;
						this._calculationId = calculationId;
						this._calculatorService.calculationId = calculationId;
					},
					error: () => {
						this.isLoading = false;
						this.hasError = true;
						this.errorLogMessage =
							"Failed to calculate emissions. Please try again later.";
					},
					finalize: () => (this.hasCalculated = true)
				}),
				switchMap(({ calculationId }) =>
					this._dashboardHardwareAssetsService.getCalculationStatus(calculationId).pipe(
						repeat({ delay: 5000 }),
						takeWhile(({ status }) => status !== "DONE" && status !== "ERROR", true),
						last()
					)
				),
				switchMap(({ status, message }) => {
					if (status === "ERROR") {
						this.hasError = true;

						if (message) this.errorLogMessage = message;

						return throwError(
							() =>
								new ApplicationError(
									"Error while getting uploading status",
									this.constructor.name,
									this.onCalculate.name,
									false
								)
						);
					}

					const overview$ = this._getEmissionOverview();
					const totalEmission$ = this._getTotalEmission();
					const emissionByScope$ = this._getEmissionByScope();
					const emissionByManufacturer$ = this._getEmissionByManufacturer();
					const calculationKpis$ = this._getCalculationKpis();

					return forkJoin({
						overview: overview$,
						total: totalEmission$,
						emissionByScope: emissionByScope$,
						emissionByManufacturer: emissionByManufacturer$,
						calculationKpis: calculationKpis$
					});
				}),
				finalize(() => (this.isLoading = false))
			)
			.subscribe();
	}

	/**
	 * Retrieves the emission overview data for the current calculation and tenant,
	 * updates the grid with the retrieved data, and manages error state for the overview table.
	 *
	 * @returns An Observable that emits the emission overview data and updates the grid.
	 *          Sets `overviewTableHasError` to `false` on success, or `true` on error.
	 */
	private _getEmissionOverview() {
		this._gridApi?.setGridOption("loading", true);

		return this._dashboardHardwareAssetsService
			.getEmissionOverview(this._calculationId, this._tenantId)
			.pipe(
				tap({
					next: overview => {
						this.overviewTableHasError = false;
						this._gridApi?.setGridOption("rowData", overview);
					},
					error: () => (this.overviewTableHasError = true),
					finalize: () => this._gridApi?.setGridOption("loading", false)
				})
			);
	}

	/**
	 * Retrieves the total emission data for the current calculation and tenant,
	 * and updates the `totalEmissionBarChart` property with the resulting chart data.
	 * Handles loading and error states for the bar chart.
	 *
	 * @returns An Observable that emits the total emission bar chart data.
	 */
	private _getTotalEmission() {
		return this._dashboardHardwareAssetsService
			.getTotalEmission(this._calculationId, this._tenantId)
			.pipe(
				tap({
					next: chart => (this.totalEmissionBarChart = chart),
					error: () =>
						(this.totalEmissionBarChart = {
							chart: {} as BarChart,
							hasError: true
						}),
					finalize: () => (this.isTotalEmissionBarChartLoading = false)
				})
			);
	}

	/**
	 * Retrieves emission data by scope for the current calculation and tenant,
	 * and updates the `emissionByScopePieChart` property with the resulting chart data.
	 * Handles loading and error states for the emission by scope pie chart.
	 *
	 * @returns An Observable that emits the emission by scope pie chart data.
	 */
	private _getEmissionByScope() {
		return this._dashboardHardwareAssetsService
			.getEmissionByScope(this._calculationId, this._tenantId)
			.pipe(
				tap({
					next: chartResponse => {
						this.emissionByScopePieChart = chartResponse;

						const totalEmission = roundValueUpTo(
							chartResponse.chart.PieChartSlice.reduce(
								(acc, slice) => acc + slice.value,
								0
							)
						);

						this._emissionByScopePieChartTotalEmissions.set(totalEmission);
					},
					error: () =>
						(this.emissionByScopePieChart = {
							chart: {} as PieChart,
							hasError: true
						}),
					finalize: () => (this.isEmissionByScopePieChartLoading = false)
				})
			);
	}

	/**
	 * Retrieves emission data grouped by manufacturer for the current calculation and tenant.
	 *
	 * This method calls the dashboard hardware assets service to fetch emission data,
	 * then updates the `emissionByModelPieChart` property with the resulting chart data.
	 * If an error occurs during the fetch, it sets `emissionByModelPieChart` to an empty chart
	 * object with an error flag. The loading state `isEmissionByModelPieChartLoading` is set to
	 * false when the operation completes, regardless of success or failure.
	 *
	 * @returns An Observable that emits the emission data as a chart object.
	 */
	private _getEmissionByManufacturer() {
		return this._dashboardHardwareAssetsService
			.getEmissionByManufacturer(this._calculationId, this._tenantId)
			.pipe(
				tap({
					next: chartResponse => {
						this.emissionByModelPieChart = chartResponse;

						const totalEmission = roundValueUpTo(
							chartResponse.chart.PieChartSlice.reduce(
								(acc, slice) => acc + slice.value,
								0
							)
						);

						const sliceCount = chartResponse.chart.PieChartSlice.length;

						this.totalEmissionPieChartColors = Array.from(
							{ length: sliceCount },
							(_, index) =>
								index === sliceCount - 1
									? COLORS.GREY_50
									: this._predefinedColors[index % this._predefinedColors.length]
						);

						this._emissionByManufacturerPieChartTotalEmissions.set(totalEmission);
					},
					error: () =>
						(this.emissionByModelPieChart = {
							chart: {} as PieChart,
							hasError: true
						}),
					finalize: () => (this.isEmissionByModelPieChartLoading = false)
				})
			);
	}

	/**
	 * Retrieves calculation KPIs for the current calculation and tenant,
	 * sets the calculation data in the calculator service, and returns
	 * an observable of the KPIs.
	 *
	 * @returns An observable emitting the calculation KPIs.
	 */
	private _getCalculationKpis() {
		return this._dashboardHardwareAssetsService
			.getCalculationKpis(this._calculationId, this._tenantId)
			.pipe(
				tap({
					next: calculationKpis => this._calculatorService.setCalculation(calculationKpis)
				})
			);
	}

	/**
	 * Fetches hardware asset options including general categories, manufacturers, and locations
	 * from the dashboard hardware assets service. The method retrieves all three sets of options
	 * in parallel, transforms them into arrays of objects with `label` and `value` properties,
	 * and assigns them to the corresponding component properties:
	 * - `generalCategoriesOptions`
	 * - `manufacturersOptions`
	 * - `locationsOptions`
	 *
	 * This method does not return a value; it subscribes internally to the observable stream.
	 *
	 * @private
	 */
	private _getHardwareAssetsOptions() {
		const generalCategories$ = this._dashboardHardwareAssetsService.getGeneralCategories().pipe(
			map(({ categories }) =>
				categories.map(category => ({
					label: category,
					value: category
				}))
			)
		);
		const manufacturers$ = this._dashboardHardwareAssetsService.getAllManufacturers().pipe(
			map(({ manufacturers }) =>
				manufacturers.map(manufacturer => ({
					label: manufacturer,
					value: manufacturer
				}))
			)
		);
		const locations$ = this._dashboardHardwareAssetsService.getAllCountries();

		forkJoin({
			generalCategories: generalCategories$,
			manufacturers: manufacturers$,
			locations: locations$
		})
			.pipe(
				tap({
					next: ({ generalCategories, manufacturers, locations }) => {
						this.generalCategoriesOptions = generalCategories;
						this.manufacturersOptions = manufacturers;
						this.locationsOptions = locations;
					}
				})
			)
			.subscribe();
	}

	/**
	 * Deletes the current calculation if a calculation ID is present.
	 *
	 * This method checks if the `_calculationId` property is defined. If so,
	 * it calls the `deleteCalculation` method of the `_dashboardHardwareAssetsService`
	 * to delete the calculation associated with the given ID.
	 * The deletion is performed asynchronously via an observable subscription.
	 *
	 * @private
	 */
	private _deleteCalculation() {
		if (!this._calculationId) return;

		this._dashboardHardwareAssetsService.deleteCalculation(this._calculationId).subscribe();
	}
}
